import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertCircle, RefreshCw, Home } from 'lucide-react';
import './error-boundary.scss';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  context?: string; // e.g., "Trip Overview", "Ideas View"
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary component to catch React errors
 * Provides user-friendly error messages and recovery options
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error Boundary caught an error:', error, errorInfo);
    }

    // TODO: Send to error logging service in production
    // Example: logErrorToService(error, errorInfo);

    this.setState({
      error,
      errorInfo
    });

    // Call custom error handler if provided
    this.props.onError?.(error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    const { hasError, error, errorInfo } = this.state;
    const { children, fallback, context } = this.props;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      // Default error UI
      return (
        <div
          className='error-boundary'
          role='alert'
          aria-live='assertive'
        >
          <div className='error-boundary-content'>
            <div className='error-icon'>
              <AlertCircle
                size={48}
                color='#dc3545'
              />
            </div>

            <h2 className='error-title'>Something went wrong</h2>

            <p className='error-message'>
              {context
                ? `An error occurred in ${context}. `
                : 'An unexpected error has occurred. '}
              We apologize for the inconvenience.
            </p>

            {process.env.NODE_ENV === 'development' && error && (
              <details className='error-details'>
                <summary>Error Details (Development Only)</summary>
                <div className='error-stack'>
                  <p>
                    <strong>Error:</strong> {error.toString()}
                  </p>
                  {errorInfo && (
                    <pre className='error-stack-trace'>
                      {errorInfo.componentStack}
                    </pre>
                  )}
                </div>
              </details>
            )}

            <div className='error-actions'>
              <button
                className='btn btn-primary'
                onClick={this.handleReset}
                aria-label='Try again'
              >
                <RefreshCw size={16} />
                Try Again
              </button>

              <button
                className='btn btn-outline-secondary'
                onClick={this.handleReload}
                aria-label='Reload page'
              >
                <RefreshCw size={16} />
                Reload Page
              </button>

              <button
                className='btn btn-outline-secondary'
                onClick={this.handleGoHome}
                aria-label='Go to homepage'
              >
                <Home size={16} />
                Go Home
              </button>
            </div>

            <p className='error-support'>
              If this problem persists, please contact{' '}
              <a href='mailto:support@travelhearts.com'>support</a>.
            </p>
          </div>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
